import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import type { SDSTableFeatures, TableData, TableProps } from '@repo/sds/components/SDSTable/types/table.types';

export function buildTableColumns<T extends TableData>(
  columns: TableProps<T>['columns'],
  sortable: boolean,
  selectable: boolean,
  renderSelectionCell: (cell: CellContext<SDSTableFeatures, T, unknown>) => ReactNode,
  renderActions: ((row: T) => ReactNode) | undefined,
  actionsLabel: string,
  actionsHeaderDataTestId: string | undefined,
): ColumnDef<SDSTableFeatures, T, unknown>[] {
  const baseCols: ColumnDef<SDSTableFeatures, T, unknown>[] = columns.map((col: TableProps<T>['columns'][number]) => {
    const cellRenderer = col.renderCell
      ? col.renderCell
      : (cell: CellContext<SDSTableFeatures, T, unknown>) => col.render(cell.row.original);

    return {
      id: col.id,
      header: col.label,
      enableSorting: sortable && col.sortable !== false,
      cell: cellRenderer,
      meta: {
        dataTestId: col.dataTestId,
        cellDataTestId: col.cellDataTestId,
        align: col.align,
        width: col.width,
      },
    };
  });

  const selectionCol: ColumnDef<SDSTableFeatures, T, unknown>[] = selectable
    ? [
        {
          id: '_selection',
          header: 'Select',
          cell: renderSelectionCell,
          size: 48,
          enableSorting: false,
        },
      ]
    : [];

  const actionsCol: ColumnDef<SDSTableFeatures, T, unknown>[] = renderActions
    ? [
        {
          id: '_actions',
          header: actionsLabel,
          cell: (cell: CellContext<SDSTableFeatures, T, unknown>) => renderActions(cell.row.original),
          size: 80,
          enableSorting: false,
          meta: {
            dataTestId: actionsHeaderDataTestId,
          },
        },
      ]
    : [];

  return [...selectionCol, ...baseCols, ...actionsCol];
}
